import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { Layer, LayersService, Project } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class ProjectStore {
    public readonly layerList = signal<Layer[]>([]);
    public readonly loading = signal(true);
    public readonly project: WritableSignal<Project | null>;

    private readonly layersService = inject(LayersService);
    private readonly projectBloc = inject(ProjectBloc);

    public constructor() {
        this.project = this.projectBloc.value;

        effect(async () => {
            this.loading.set(true);
            try {
                await this.fetchAllLayers(this.project());
            } finally {
                this.loading.set(false);
            }
        });
    }

    public async refresh(): Promise<void> {
        const currentProject = this.project();
        if (!currentProject) {
            return;
        }
        this.loading.set(true);
        try {
            await this.fetchAllLayers(currentProject);
        } finally {
            this.loading.set(false);
        }
    }

    private async fetchAllLayers(project: Project | null): Promise<void> {
        const layers: Layer[] = [];
        let needFetch = true;
        let page = 0;

        if (!project) {
            return;
        }

        while (needFetch) {
            const response = await firstValueFrom(
                this.layersService.layerControllerGet({
                    page: page,
                    project: project.id,
                    take: 50,
                }),
            );

            layers.push(...response.items);

            if (!response.hasNextPage) {
                needFetch = false;
                continue;
            }

            page += 1;
        }

        this.layerList.set(layers.sort((a: Layer, b: Layer) => a.name.localeCompare(b.name)));
    }
}
